import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { AsterTerminalChart, type ChartMode } from "../components/AsterTerminalChart";
import { CryptoAssetPicker } from "../components/CryptoAssetPicker";
import { LoadingTerminal } from "../components/LoadingTerminal";
import { brl, compactBrl, datePt, money, percent } from "../format";
import type { CryptoAsset, DashboardData, MarketData } from "../types";

const POPULAR_COINS: CryptoAsset[] = [
  { id: "bitcoin", name: "Bitcoin", symbol: "BTC", marketCapRank: 1 },
  { id: "ethereum", name: "Ethereum", symbol: "ETH", marketCapRank: 2 },
  { id: "solana", name: "Solana", symbol: "SOL", marketCapRank: 6 },
  { id: "binancecoin", name: "BNB", symbol: "BNB", marketCapRank: 5 },
  { id: "cardano", name: "Cardano", symbol: "ADA", marketCapRank: 18 },
];

const LOCAL_GENESIS: DashboardData = {
  token: {
    name: "ASTER", network: "Asterum Private Network", maxSupply: 250_000,
    circulatingSupply: 240_000, walletBalance: 40_000, walletAddress: "0xPAI...ASTER",
    currentPrice: 1, walletValue: 40_000, returnValue: 0, returnPercent: 0,
  },
  latest: null,
  closings: [{ time: "2026-08-01", value: 1, valuation: 250_000, note: "Preço gênesis" }],
  events: [{ id: "genesis", date: "2026-08-01", type: "GENESIS BLOCK", title: "Rede ASTER iniciada", detail: "250.000 ASTER criados. Max supply fixado e sem mint ou burn.", amount: 250_000 }],
};

function signedPercent(value: number, decimals = 2) {
  return `${value >= 0 ? "+" : ""}${percent(value, decimals)}`;
}

function marketMoney(value: number | undefined, currency: "BRL" | "USD") {
  if (value === undefined) return "—";
  const decimals = value < 1 ? 6 : value < 100 ? 4 : 2;
  return money(value, currency, decimals);
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [asset, setAsset] = useState<CryptoAsset>(POPULAR_COINS[0]);
  const [days, setDays] = useState(90);
  const [currency, setCurrency] = useState<"BRL" | "USD">(() => localStorage.getItem("aster-currency") === "USD" ? "USD" : "BRL");
  const [mode, setMode] = useState<ChartMode>("price");
  const [market, setMarket] = useState<MarketData | null>(null);
  const [marketStatus, setMarketStatus] = useState("Conectando ao mercado…");
  const [lastFx, setLastFx] = useState(5.5);
  const [grid, setGrid] = useState(true);
  const [resetNonce, setResetNonce] = useState(0);

  useEffect(() => {
    api<DashboardData>("/api/dashboard")
      .then(setData)
      .catch(() => setData(import.meta.env.DEV ? LOCAL_GENESIS : null));
  }, []);

  useEffect(() => {
    localStorage.setItem("aster-currency", currency);
    const controller = new AbortController();
    const params = new URLSearchParams({
      coin: asset.id,
      symbol: asset.symbol,
      name: asset.name,
      days: String(days),
      currency: currency.toLowerCase(),
    });
    if (asset.marketCapRank) params.set("rank", String(asset.marketCapRank));
    api<MarketData>(`/api/market?${params}`, { signal: controller.signal })
      .then((payload) => {
        setMarket(payload);
        setLastFx(payload.fx.usdBrl);
        setMarketStatus(`${payload.source} · ${payload.coverage.pointCount} fechamentos diários`);
      })
      .catch((error: Error) => {
        if (error.name !== "AbortError") setMarketStatus(`${error.message} · ASTER permanece disponível`);
      });
    return () => controller.abort();
  }, [asset, days, currency]);

  const usdBrl = market?.fx.usdBrl ?? lastFx;
  const current = data?.token.currentPrice ?? 1;
  const displayedPrice = currency === "BRL" ? current : current / usdBrl;
  const alternatePrice = currency === "BRL" ? money(current / usdBrl, "USD", 4) : money(current, "BRL", 4);
  const latest = data?.latest;
  const redemptionCoverage = data?.token.walletValue ? Math.min(1, (latest?.excessCash ?? 0) / data.token.walletValue) : 0;

  const visibleClosings = useMemo(() => {
    if (!data) return [];
    const cutoff = new Date();
    cutoff.setUTCDate(cutoff.getUTCDate() - days);
    const cutoffIso = cutoff.toISOString().slice(0, 10);
    const filtered = data.closings.filter((point) => point.time >= cutoffIso);
    return filtered.length ? filtered : data.closings.slice(-1);
  }, [data, days]);

  const asterWindowChange = useMemo(() => {
    const first = visibleClosings[0]?.value;
    const last = visibleClosings.at(-1)?.value;
    return first && last ? last / first - 1 : 0;
  }, [visibleClosings]);

  async function fullscreen() {
    const terminal = document.getElementById("aster-terminal");
    if (!terminal) return;
    if (document.fullscreenElement) await document.exitFullscreen();
    else await terminal.requestFullscreen();
  }

  if (!data) return <LoadingTerminal message="Carregando fechamentos realizados…" />;

  return (
    <main className="market-shell">
      <section className="market-ticker">
        <div className="asset-identity">
          <img src="/brand/asterum-icon-white.svg" alt="" />
          <div><strong>ASTER / {currency}</strong><small>Asterum Private Network · preço observado</small></div>
        </div>
        <div className="ticker-price">
          <strong>{money(displayedPrice, currency, 4)}</strong>
          <span className={data.token.returnPercent >= 0 ? "text-positive" : "text-negative"}>{signedPercent(data.token.returnPercent)}</span>
          <small>{alternatePrice}</small>
        </div>
        <div className="ticker-stat"><small>Valuation</small><strong>{compactBrl(latest?.valuation ?? 250_000)}</strong></div>
        <div className="ticker-stat"><small>Último fechamento</small><strong>{latest?.periodEnd ? datePt(latest.periodEnd) : "Gênesis"}</strong></div>
        <div className="ticker-stat usd-rate"><small>USD/BRL hoje</small><strong>R$ {usdBrl.toFixed(4)}</strong><span>{market?.fx.source ?? "última cotação válida"}</span></div>
        <div className="currency-switch" aria-label="Moeda do painel">
          <button className={currency === "BRL" ? "active" : ""} onClick={() => { setMarket(null); setMarketStatus(`Carregando ${asset.symbol}/BRL…`); setCurrency("BRL"); }}>R$ BRL</button>
          <button className={currency === "USD" ? "active" : ""} onClick={() => { setMarket(null); setMarketStatus(`Carregando ${asset.symbol}/USD…`); setCurrency("USD"); }}>$ USD</button>
        </div>
      </section>

      <section id="aster-terminal" className="terminal-panel">
        <div className="terminal-main">
          <div className="chart-commandbar">
            <div className="symbol-chip"><span>ASTER</span><b>{currency}</b><i>observado</i></div>
            <div className="mode-tabs">
              <button className={mode === "price" ? "active" : ""} onClick={() => setMode("price")} title="Exibir o preço real do ASTER">Preço</button>
              <button className={mode === "performance" ? "active" : ""} onClick={() => setMode("performance")} title="Comparar variação percentual">Comparação %</button>
            </div>
            <CryptoAssetPicker
              value={asset}
              popular={POPULAR_COINS}
              onSelect={(selected, quote) => {
                setMarket(null);
                setMarketStatus(`Carregando ${selected.symbol}/${quote}…`);
                setAsset(selected);
                setCurrency(quote);
                setMode("performance");
              }}
            />
            <div className="range-tabs" aria-label="Período do gráfico">
              {[[30, "1M"], [90, "3M"], [180, "6M"], [365, "1A"], [730, "24M"]].map(([range, label]) => <button key={range} className={days === range ? "active" : ""} onClick={() => { setMarket(null); setMarketStatus(`Carregando ${asset.symbol}/${currency}…`); setDays(Number(range)); }}>{label}</button>)}
            </div>
            <div className="chart-tools">
              <button className={grid ? "active" : ""} onClick={() => setGrid((value) => !value)} title="Mostrar ou ocultar grade" aria-label="Alternar grade">#</button>
              <button onClick={() => setResetNonce((value) => value + 1)} title="Reenquadrar gráfico" aria-label="Reenquadrar gráfico">⌖</button>
              <button onClick={fullscreen} title="Tela cheia" aria-label="Abrir gráfico em tela cheia">⛶</button>
            </div>
          </div>
          <div className="chart-readout">
            <span>{mode === "price" ? `ASTER / ${currency}` : `ASTER vs. ${asset.symbol}`}</span>
            <strong>{mode === "price" ? money(displayedPrice, currency, 4) : `ASTER ${signedPercent(asterWindowChange)} · ${asset.symbol} ${market ? signedPercent(market.stats.changePercent) : "…"}`}</strong>
            <small>{mode === "price" ? "Preço econômico por unidade" : "Retorno de cada série desde seu próprio primeiro ponto visível"}</small>
          </div>
          <AsterTerminalChart aster={visibleClosings} crypto={market?.points ?? []} cryptoName={asset.symbol} currency={currency} usdBrl={usdBrl} mode={mode} showGrid={grid} resetNonce={resetNonce} />

          <div className="market-data-strip" aria-label="Cobertura e procedência dos dados">
            <span><b>{asset.symbol}/{currency}</b><small>{market ? `${market.coverage.pointCount} pontos · ${market.coverage.availableFrom ? datePt(market.coverage.availableFrom) : "—"} → ${market.coverage.availableTo ? datePt(market.coverage.availableTo) : "—"}` : "carregando histórico"}</small></span>
            <span><b>{market?.coverage.complete ? "Cobertura completa" : market ? "Cobertura parcial" : "Verificando cobertura"}</b><small>{market ? `Diário · janela de ${market.coverage.requestedDays} dias` : "Validação em andamento"}</small></span>
            <span><b>ASTER observado</b><small>{data.closings.length} fechamento(s) · desde {datePt(data.closings[0].time)}</small></span>
          </div>

          <div className="terminal-statusbar">
            <span><i className={market?.points.length ? "status-online" : "status-waiting"} /> {marketStatus}</span>
            <span>Role para zoom · arraste para navegar · cursor para inspecionar</span>
            <span>{mode === "price" ? `Eixo em ${currency} · valor real` : "Eixo em variação % · bases independentes"}</span>
          </div>
        </div>

        <aside className="market-sidebar">
          <section className="sidebar-block performance-block">
            <div className="sidebar-head"><span>Proof of Performance</span><b>Realizado</b></div>
            <div className="proof-metrics">
              <div><small>Receita líquida UDM</small><strong>{brl(latest?.netRevenue ?? 0)}</strong></div>
              <div><small>EBITDA ajustado UDM</small><strong className={(latest?.adjustedEbitda ?? 0) >= 0 ? "text-positive" : "text-negative"}>{brl(latest?.adjustedEbitda ?? 0)}</strong></div>
              <div><small>Valor operacional</small><strong>{brl(latest?.conservativeOperationalValue ?? 200_000)}</strong></div>
              <div><small>Caixa excedente</small><strong>{brl(latest?.excessCash ?? 0)}</strong></div>
            </div>
          </section>

          <section className="sidebar-block comparison-block">
            <div className="sidebar-head"><span>Ativo comparado</span><b className={market?.coverage.complete ? "" : "is-warning"}>{market?.coverage.complete ? "auditado" : "checando"}</b></div>
            <div className="comparison-identity"><strong>{asset.symbol}/{currency}</strong><span>{asset.name}</span></div>
            <div className="comparison-metrics">
              <div><small>Último diário</small><strong>{marketMoney(market?.stats.last, currency)}</strong></div>
              <div><small>Retorno no período</small><strong className={(market?.stats.changePercent ?? 0) >= 0 ? "text-positive" : "text-negative"}>{market ? signedPercent(market.stats.changePercent) : "—"}</strong></div>
              <div><small>Máxima</small><strong>{marketMoney(market?.stats.high, currency)}</strong></div>
              <div><small>Mínima</small><strong>{marketMoney(market?.stats.low, currency)}</strong></div>
            </div>
            <div className="market-provenance">
              <span><small>Fonte efetiva</small>{market?.sourceUrl ? <a href={market.sourceUrl} target="_blank" rel="noreferrer">{market.source} ↗</a> : <b>Conectando…</b>}</span>
              <span><small>Método</small><b>{market?.pair.quoteMethod === "aggregated" ? "Referência agregada" : market?.pair.quoteMethod === "usd-market" ? `Mercado USD${currency === "BRL" ? " + câmbio diário" : ""}` : market ? "Mercado USDT + câmbio diário" : "—"}</b></span>
              {market?.warnings.length ? <p>{market.warnings.join(" ")}</p> : null}
            </div>
          </section>

          <section className="sidebar-block wallet-block">
            <div className="sidebar-section-title">Pai Wallet</div>
            <div className="wallet-card-compact">
              <span className="wallet-avatar">P</span>
              <div><small>Saldo</small><strong>{data.token.walletBalance.toLocaleString("pt-BR")} ASTER</strong></div>
              <div><small>Valor da posição</small><strong>{currency === "BRL" ? brl(data.token.walletValue) : money(data.token.walletValue / usdBrl, "USD", 2)}</strong></div>
              <div><small>Resultado</small><strong className={data.token.returnValue >= 0 ? "text-positive" : "text-negative"}>{data.token.returnValue >= 0 ? "+" : ""}{currency === "BRL" ? brl(data.token.returnValue) : money(data.token.returnValue / usdBrl, "USD", 2)}</strong></div>
            </div>
          </section>
          <section className="sidebar-block network-block">
            <div className="sidebar-section-title">Rede</div>
            <div className="network-mini"><span><small>Max supply</small><b>{data.token.maxSupply.toLocaleString("pt-BR")}</b></span><span><small>Circulante</small><b>{data.token.circulatingSupply.toLocaleString("pt-BR")}</b></span></div>
            <div className="supply-bar"><span style={{ width: `${data.token.circulatingSupply / data.token.maxSupply * 100}%` }} /></div>
            <a className="sidebar-link" href="/metodologia">Ver fórmula e governança →</a>
          </section>
        </aside>
      </section>

      <section className="dashboard-lower-grid">
        <article className="panel reserve-panel">
          <div className="panel-head"><div><span className="section-kicker">Liquidez interna</span><h2>Capacidade de resgate</h2></div><strong>{percent(redemptionCoverage, 0)}</strong></div>
          <div className="progress-track"><span style={{ width: `${redemptionCoverage * 100}%` }} /></div>
          <div className="reserve-values"><div><small>Carteira econômica</small><strong>{brl(data.token.walletValue)}</strong></div><div><small>Caixa excedente</small><strong>{brl(latest?.excessCash ?? 0)}</strong></div></div>
          <p>O valor econômico não representa liquidez imediata. A reserva operacional é preservada antes de qualquer recompra.</p>
        </article>
        <article className="panel event-panel compact-events">
          <div className="panel-head"><div><span className="section-kicker">Histórico publicado</span><h2>Eventos da rede</h2></div><a className="secondary-button" href="/api/export">Exportar CSV</a></div>
          <div className="event-list">{data.events.slice(0, 4).map((event) => <article key={event.id}><time>{datePt(event.date)}</time><span className="event-node" /><div><small>{event.type}</small><h3>{event.title}</h3><p>{event.detail}</p></div></article>)}</div>
        </article>
      </section>
    </main>
  );
}
